import { useEffect, useRef, useState } from 'react';
import { Upload, UserSquare2, Sparkles, Loader2, AlertCircle, Image as ImageIcon, Check } from 'lucide-react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { StyleSelector, styleLabel } from '@/components/StyleSelector';

const FRAMINGS = [{ id: 'close_up', label: 'Close-up' }, { id: 'medium', label: 'Medium' }, { id: 'waist_up', label: 'Waist-up' }];
const BG_TYPES = [
  { id: 'plain', label: 'Studio colour' }, { id: 'location', label: 'Location' }, { id: 'text', label: 'Describe' }, { id: 'keep', label: 'Keep photo', uploadOnly: true },
];
const SWATCHES = ['#1b1f2a', '#0b0b0f', '#f4f1ea', '#dfe7f1', '#2f4858', '#6b2d5c', '#f2a65a', '#1f7a5c'];

export const PresenterAvatarPanel = ({ video, onUpdate, onRefresh }) => {
  const av = video.avatar || {};
  const bg = av.background || {};
  const [chars, setChars] = useState([]);
  const [locs, setLocs] = useState([]);
  const [showStyle, setShowStyle] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/workspace/characters').then(r => setChars((r.data.characters || []).filter(c => c.image_storage_path))).catch(() => {});
    api.get('/workspace/locations').then(r => setLocs(r.data.locations || [])).catch(() => {});
  }, []);

  const setAvatar = (patch) => onUpdate({ avatar: patch }, { silent: true });
  const setBg = (patch) => onUpdate({ avatar: { background: patch } }, { silent: true });

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try { await api.post(`/presenter/${video.id}/avatar/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); await onRefresh(); toast.success('Photo uploaded'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const generate = async () => {
    try { await api.post(`/presenter/${video.id}/avatar/generate`); await onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail?.split('|').pop() || 'Could not start'); }
  };

  const generating = video.image_status === 'generating';
  const keepPhoto = av.source === 'upload' && bg.type === 'keep';
  const selectedChar = chars.find(c => c.id === av.character_id);

  return (
    <section className="ff-card rounded-2xl p-5" data-testid="presenter-avatar-panel">
      <h2 className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#E879F9] flex items-center gap-2 mb-3"><UserSquare2 className="w-3.5 h-3.5" /> 2 · Avatar</h2>

      <div className="grid grid-cols-[1fr_auto] gap-4">
        <div className="space-y-3 min-w-0">
          <div className="flex gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06] w-fit">
            {[{ id: 'character', label: 'Workspace character' }, { id: 'upload', label: 'Upload a photo' }].map(o => (
              <button key={o.id} data-testid={`avatar-source-${o.id}`} onClick={() => setAvatar({ source: o.id, ...(o.id === 'character' && bg.type === 'keep' ? { background: { type: 'plain' } } : {}) })}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${av.source === o.id ? 'bg-[#E879F9]/15 text-white' : 'text-[#8A8A93] hover:text-white'}`}>{o.label}</button>
            ))}
          </div>

          {av.source === 'character' ? (
            <select data-testid="avatar-character-select" value={av.character_id || ''} onChange={e => setAvatar({ character_id: e.target.value || null })} className="ff-input w-full rounded-lg px-3 py-2 text-sm">
              <option value="">Pick a character…</option>
              {chars.map(c => <option key={c.id} value={c.id}>{c.name}{c.project_name ? ` · ${c.project_name}` : ''}</option>)}
            </select>
          ) : (
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="avatar-upload-input" />
              <button data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="ff-btn-secondary text-xs flex items-center gap-2">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {av.uploaded_path ? 'Replace photo' : 'Choose photo'}
              </button>
              {av.uploaded_path && <span className="text-[11px] text-[#10B981] flex items-center gap-1"><Check className="w-3 h-3" /> Photo ready</span>}
            </div>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#8A8A93] mb-1.5">Framing</p>
            <div className="flex gap-1.5">
              {FRAMINGS.map(f => (
                <button key={f.id} data-testid={`avatar-framing-${f.id}`} onClick={() => setAvatar({ framing: f.id })} disabled={keepPhoto}
                  className={`px-2.5 py-1 rounded-md text-[11px] border transition-colors disabled:opacity-40 ${av.framing === f.id ? 'border-[#E879F9]/50 bg-[#E879F9]/10 text-white' : 'border-white/[0.08] text-[#C9C9CF] hover:border-white/25'}`}>{f.label}</button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#8A8A93] mb-1.5">Background</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {BG_TYPES.filter(b => !b.uploadOnly || av.source === 'upload').map(b => (
                <button key={b.id} data-testid={`avatar-bg-${b.id}`} onClick={() => setBg({ type: b.id })}
                  className={`px-2.5 py-1 rounded-md text-[11px] border transition-colors ${bg.type === b.id ? 'border-[#E879F9]/50 bg-[#E879F9]/10 text-white' : 'border-white/[0.08] text-[#C9C9CF] hover:border-white/25'}`}>{b.label}</button>
              ))}
            </div>
            {bg.type === 'plain' && (
              <div className="flex items-center gap-1.5">
                {SWATCHES.map(c => (
                  <button key={c} data-testid={`avatar-bg-color-${c.slice(1)}`} onClick={() => setBg({ color: c })} style={{ background: c }}
                    className={`w-6 h-6 rounded-full border-2 ${bg.color === c ? 'border-[#E879F9]' : 'border-white/10'}`} aria-label={c} />
                ))}
                <input type="color" value={bg.color || '#1b1f2a'} onChange={e => setBg({ color: e.target.value })} className="w-6 h-6 rounded-full bg-transparent border-0 cursor-pointer" data-testid="avatar-bg-color-custom" />
              </div>
            )}
            {bg.type === 'location' && (
              <select data-testid="avatar-location-select" value={bg.location_id || ''} onChange={e => setBg({ location_id: e.target.value || null })} className="ff-input w-full rounded-lg px-3 py-2 text-sm">
                <option value="">Pick a location…</option>
                {locs.map(l => <option key={l.id} value={l.id}>{l.name}{l.project_name ? ` · ${l.project_name}` : ''}</option>)}
              </select>
            )}
            {bg.type === 'text' && (
              <input data-testid="avatar-bg-text" defaultValue={bg.text || ''} onBlur={e => setBg({ text: e.target.value })} placeholder="e.g. sunlit loft office with plants, bookshelves out of focus"
                className="ff-input w-full rounded-lg px-3 py-2 text-sm" />
            )}
            {bg.type === 'keep' && <p className="text-[11px] text-[#8A8A93]">Your photo is used exactly as uploaded — no re-generation, no style applied.</p>}
          </div>

          {!keepPhoto && (
            <div className="flex flex-wrap items-center gap-2">
              <button data-testid="avatar-style-btn" onClick={() => setShowStyle(s => !s)} className="text-[11px] px-2.5 py-1 rounded-md border border-white/[0.08] text-[#C9C9CF] hover:border-white/25">
                Style · <b className="text-white">{styleLabel(av.visual_style)}</b>
              </button>
              <select data-testid="avatar-image-provider" value={av.image_provider || 'openai'} onChange={e => setAvatar({ image_provider: e.target.value })} className="ff-input rounded-md px-2 py-1 text-[11px]">
                <option value="openai">GPT Image (best likeness)</option>
                <option value="gemini">Gemini</option>
              </select>
              <select data-testid="avatar-aspect" value={video.aspect_ratio || '16:9'} onChange={e => onUpdate({ aspect_ratio: e.target.value }, { silent: true })} className="ff-input rounded-md px-2 py-1 text-[11px]">
                <option value="16:9">16:9 landscape</option>
                <option value="9:16">9:16 vertical</option>
                <option value="1:1">1:1 square</option>
              </select>
            </div>
          )}
          {showStyle && !keepPhoto && <StyleSelector value={av.visual_style} onChange={(v) => { setAvatar({ visual_style: v }); }} columns="grid-cols-2 sm:grid-cols-3" testIdPrefix="avstyle" />}

          <button data-testid="avatar-generate-btn" onClick={generate} disabled={generating || (av.source === 'character' ? !av.character_id : !av.uploaded_path)}
            className="ff-btn-primary text-xs flex items-center gap-2 disabled:opacity-50">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {keepPhoto ? 'Use this photo' : av.image_path ? 'Regenerate avatar image' : 'Create avatar image'}
          </button>
          {video.image_status === 'failed' && <p data-testid="avatar-error" className="text-[11px] text-red-400 flex items-start gap-1.5"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" /> {video.image_error}</p>}
        </div>

        <div className="w-40 sm:w-52 shrink-0">
          <div className={`${video.aspect_ratio === '9:16' ? 'aspect-[9/16]' : video.aspect_ratio === '1:1' ? 'aspect-square' : 'aspect-video'} rounded-xl bg-[#121216] border border-white/[0.06] overflow-hidden flex items-center justify-center relative`} data-testid="avatar-preview">
            {av.image_path ? <img src={getImageUrl(av.image_path)} alt="Avatar" className="w-full h-full object-cover" />
              : selectedChar?.image_storage_path ? <img src={getImageUrl(selectedChar.image_storage_path)} alt="" className="w-full h-full object-cover opacity-40" />
              : <ImageIcon className="w-7 h-7 text-[#55555C]" />}
            {generating && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#E879F9] animate-spin" /></div>}
          </div>
          <p className="text-[10px] text-[#8A8A93] mt-1.5 text-center">{av.image_path ? 'Avatar image' : selectedChar ? 'Reference (not yet composed)' : 'No image yet'}</p>
        </div>
      </div>
    </section>
  );
};
